// Inside the Krakens window: the chooser (King Octo / Queen Octo) and one Kraken's org — needs, direct giving, their page.
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, ExternalLink, Heart, Sparkles, Share2 } from "lucide-react";
import { WishItem, abs } from "./KrakonDock";

const shortName = (name = "") => name.replace(/^the\s+/i, "").replace(/\s+(foundation|victims advocacy foundation)$/i, "").trim() || name;

export const GiveButton = ({ k, big = false }) => k.donate?.url ? (
  <a href={k.donate.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
    className={`inline-flex items-center justify-center gap-1.5 rounded-full bg-[#F5D06F] text-[#06202B] font-black no-underline hover:brightness-105 ${big ? "px-5 py-2.5 text-sm" : "px-2.5 py-1 text-[11px]"}`}
    data-testid={`kraken-give-${big ? "big-" : ""}${k.kraken_id}`}>
    <Heart className={big ? "h-4 w-4" : "h-3 w-3"} /> Give to {shortName(k.org?.name)}
  </a>
) : null;

export const HoardNote = ({ text }) => text ? (
  <p className="m-0 rounded-2xl bg-[#F5D06F]/10 ring-1 ring-[#F5D06F]/35 px-3 py-2.5 text-xs leading-snug text-[#FBE9B5] flex gap-2" data-testid="kraken-hoard-note">
    <Sparkles className="h-4 w-4 shrink-0 text-[#F5D06F]" />
    <span>{text} <Link to="/how-it-works" className="font-bold text-[#F5D06F] underline">How it works</Link></span>
  </p>
) : null;

const KrakenCard = ({ k, onPick }) => {
  const open = (k.org?.wishlist || []).filter((w) => !w.fulfilled).length;
  return (
    <div role="button" tabIndex={0} onClick={() => onPick(k.kraken_id)} onKeyDown={(e) => e.key === "Enter" && onPick(k.kraken_id)}
      className="group rounded-2xl bg-white/[0.05] ring-1 ring-[#7FE3D6]/25 p-3 flex items-center gap-3 cursor-pointer transition-colors hover:bg-white/[0.09] hover:ring-[#7FE3D6]/60"
      data-testid={`kraken-card-${k.kraken_id}`}>
      <span className="relative shrink-0">
        <img src={abs(k.badge_url)} alt={k.title} className="h-20 w-20 rounded-full object-cover ring-2 ring-[#7FE3D6] shadow-xl transition-transform group-hover:scale-105" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-[10px] font-black uppercase tracking-[0.25em] text-[#7FE3D6]">{k.title}</p>
        <p className="m-0 mt-0.5 font-['Playfair_Display'] text-lg font-bold leading-tight">{k.org?.name}</p>
        <p className="m-0 mt-0.5 text-[11px] text-[#BFE9E2]/80 leading-snug line-clamp-2">{k.org?.summary}</p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <GiveButton k={k} />
          <span className="text-[11px] font-bold text-[#BFE9E2]/80">{open ? `${open} open wish${open > 1 ? "es" : ""}` : "See their work"}</span>
        </div>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-[#7FE3D6] transition-transform group-hover:translate-x-0.5" />
    </div>
  );
};

export const KrakenChooser = ({ data, onPick }) => (
  <div className="space-y-3" data-testid="kraken-chooser">
    {data.intro && <p className="m-0 text-xs leading-snug text-[#BFE9E2]" data-testid="kraken-intro">{data.intro}</p>}
    {data.krakens.map((k) => <KrakenCard key={k.kraken_id} k={k} onPick={onPick} />)}
    <HoardNote text={data.hoard_note} />
  </div>
);

export const KrakenDetail = ({ k, data, onBack, onLeave }) => {
  const o = k.org;
  const wishes = o.wishlist || [];
  return (
    <div className="space-y-3" data-testid={`kraken-detail-${k.kraken_id}`}>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#7FE3D6] hover:underline" data-testid="kraken-back"><ArrowLeft className="h-3.5 w-3.5" /> Both Krakens</button>
      {o.summary && <p className="m-0 text-sm leading-snug text-[#E6FAF7]" data-testid="kraken-detail-summary">{o.summary}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <GiveButton k={k} big />
        <Link to={o.page} onClick={onLeave} className="inline-flex items-center gap-1 rounded-full ring-1 ring-[#7FE3D6]/50 px-4 py-2 text-xs font-bold text-[#7FE3D6] no-underline hover:bg-white/[0.06]" data-testid={`kraken-page-${k.kraken_id}`}>Their page on THE WALL <ExternalLink className="h-3 w-3" /></Link>
      </div>
      <HoardNote text={data.hoard_note} />
      <p className="m-0 pt-1 text-[10px] font-black uppercase tracking-[0.25em] text-[#7FE3D6]">What they need right now</p>
      {wishes.length ? (
        <ul className="m-0 p-0 list-none space-y-1.5" data-testid={`kraken-wishes-${k.kraken_id}`}>
          {wishes.map((w) => <WishItem key={w.wish_id} w={w} org={o} compact />)}
        </ul>
      ) : <p className="m-0 text-xs text-[#BFE9E2]/80" data-testid={`kraken-no-wishes-${k.kraken_id}`}>Their wishlist is on its way. Giving directly helps right now.</p>}
      <p className="m-0 pt-1 text-[11px] text-[#BFE9E2]/70">Part of {o.name}? <Link to="/my-org" onClick={onLeave} className="underline font-bold text-[#7FE3D6]" data-testid="kraken-my-org">Add or update your wishlist →</Link></p>
      {k.share_slug && <Link to={`/${k.share_slug}/kraken`} onClick={onLeave} className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] ring-1 ring-white/15 px-3 py-1.5 text-[11px] font-black text-[#F5D06F] no-underline hover:bg-white/[0.1]" data-testid={`kraken-share-link-${k.kraken_id}`}><Share2 className="h-3.5 w-3.5" /> Share {k.title}</Link>}
    </div>
  );
};
